import { useDeletePageProducts } from "../../../../data";
import { useConfirmation } from "../../../components/hooks/useConfirmation";
import { DeleteIcon } from "../../../icons/DeleteIcon";
import type { MenuConfig } from "../../types";
import type { ActionContext } from "../../../actions/types";
import type { PageProductsFragmentFragment } from "../../../../__generated__/gql/graphql";
import { colParams } from "../../../utils";
import { all, any, none, anyColumn, column, featureDisabled } from "../../disabled-helpers";

function getSelectedColumnIds(ctx: ActionContext): string[] {
  const ids = Array.from(
    new Set(
      (ctx.selectedRange || [])
        .flat()
        .map((x) => x.columnId)
        .concat((ctx.rangeSelection || []).map((x) => x.columnId)),
    ),
  );
  if (ids.length === 0 && ctx.columnId) ids.push(ctx.columnId);
  return ids;
}

function computeSelectionMeta(
  selectedColumnIds: string[],
  products: PageProductsFragmentFragment[],
): {
  selectedIds: string[];
  plural: boolean;
  name: string;
} {
  const selectedRecords = products.filter((p) => selectedColumnIds.includes(p.grid_id || p.id || ""));
  const selectedIds = selectedRecords.map((p) => p.id).filter(Boolean);
  const plural = selectedColumnIds.length > 1;
  const name = plural ? "Remove columns" : "Remove column";
  return { selectedIds, plural, name };
}

export const useRemoveColumnDialog = () => {
  const remove = useDeletePageProducts();
  const confirm = useConfirmation();

  return (ids: string | string[]) => {
    const idArray = Array.isArray(ids) ? ids : [ids];
    const count = idArray.length;
    const plural = count > 1;

    confirm({
      title: plural ? `Remove ${count} Columns from Page?` : "Remove Column from Page?",
      message: plural
        ? `You're about to remove ${count} selected Columns from your Page. This action can't be undone.`
        : "You're about to remove the selected Column from your Page. This action can't be undone.",
      confirmButtonText: plural ? "Remove Columns" : "Remove Column",
      variant: "danger",
      dontShowAgainKey: plural
        ? "dont_show_remove_multiple_curves_confirmation_dialog"
        : "dont_show_remove_curve_confirmation_dialog",
      onConfirm: () => {
        idArray.forEach((id: string) => {
          if (id) remove.mutate(id);
        });
      },
    });
  };
};

export const removeColumnSection: MenuConfig[] = [
  {
    id: "remove-column",
    label: (ctx) => {
      const selectedColumnIds = getSelectedColumnIds(ctx);
      const { name } = computeSelectionMeta(selectedColumnIds, ctx.pageProductsData || []);
      return name;
    },
    icon: <DeleteIcon />,
    disabled: any(
      anyColumn(column.isDefaultAuxiliaryColumn),
      none(anyColumn(column.isAuxiliary), all(anyColumn(column.isProduct), none(featureDisabled("removeCurve")))),
    ),
    tooltip: (ctx) => {
      if (anyColumn(column.isProduct)(ctx) && featureDisabled("removeCurve")(ctx)) {
        return "Please upgrade your subscription to access this feature";
      }
      return undefined;
    },
    action: (ctx) => () => {
      const {
        api,
        columnId,
        pageProductsData,
        deletePageProducts,
        dontShowRemoveCurveWarning,
        openRemoveColumnDialog,
      } = ctx;

      const product = pageProductsData?.find((p: PageProductsFragmentFragment) => p.grid_id === columnId);
      const id = product?.id || colParams(api.getColumn(columnId))?.gridId;
      const selectedColumnIds = getSelectedColumnIds(ctx);
      const { selectedIds } = computeSelectionMeta(selectedColumnIds, pageProductsData || []);

      if (dontShowRemoveCurveWarning) {
        if (selectedIds.length > 1) {
          selectedIds.forEach((removeId: string) => {
            if (removeId) deletePageProducts.mutate(removeId);
          });
        } else if (selectedIds.length === 1) {
          selectedIds.forEach((removeId: string) => {
            if (removeId) deletePageProducts.mutate(removeId);
          });
        } else if (id) {
          deletePageProducts.mutate(id);
        }
        return;
      }

      if (selectedIds.length > 1) {
        openRemoveColumnDialog(selectedIds);
        return;
      }
      if (selectedIds.length === 1) {
        openRemoveColumnDialog(selectedIds[0]);
        return;
      }
      if (id) {
        openRemoveColumnDialog(id);
      }
    },
  },
];
